
import React, { useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { TeamEditDialog } from '@/components/admin/TeamEditDialog';
import { useTeamEdit } from '@/hooks/useTeamEdit';
import { TeamBreadcrumb } from '@/components/admin/team/TeamBreadcrumb';
import { TeamPageHeader } from '@/components/admin/team/TeamPageHeader';
import { TeamInfoCard } from '@/components/admin/team/TeamInfoCard';
import { TeamManagementTabs } from '@/components/admin/team/TeamManagementTabs';
import { TeamDeleteDialog } from '@/components/admin/team/TeamDeleteDialog';
import { PaymentDashboard } from '@/components/payment/PaymentDashboard';
import { TransactionHistoryTable } from '@/components/payment/TransactionHistoryTable';

const SuperAdminTeam = () => {
  const { teamId } = useParams();
  const { profile, loading, isImpersonating, effectiveRole } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const { data: team, isLoading: teamLoading } = useQuery({
    queryKey: ['team', teamId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('teams')
        .select(`
          *,
          clubs!inner(
            id,
            name,
            organizations (
              id,
              name
            )
          ),
          seasons(
            id,
            name,
            season_year,
            status
          ),
          divisions(
            id,
            name
          ),
          team_coaches(
            id,
            role,
            is_active,
            profiles(
              id,
              first_name,
              last_name
            )
          )
        `)
        .eq('id', teamId)
        .eq('team_coaches.is_active', true)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!teamId,
  });

  // Query for player count
  const { data: playerCount = 0 } = useQuery({
    queryKey: ['team-player-count', teamId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true })
        .eq('team_id', teamId);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!teamId,
  });

  const teamEditHook = useTeamEdit(team || null);

  const deleteTeamMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Team deleted successfully');
      // Navigate back to the club page
      if (team?.clubs?.id) {
        navigate(`/superadmin/club/${team.clubs.id}`);
      } else {
        navigate('/superadmin');
      }
    },
    onError: (error) => {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    },
  });

  const handleDelete = () => {
    setDeleteConfirmText('');
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmText === team?.name) {
      deleteTeamMutation.mutate();
      setIsDeleteDialogOpen(false);
    }
  };

  // Redirect if not superadmin or if currently impersonating
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile || effectiveRole !== 'superadmin' || isImpersonating) {
    return <Navigate to="/" replace />;
  }

  if (teamLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">Loading team...</div>
        </div>
      </div>
    );
  }

  // Add safety check for team data
  if (!team) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Team not found</h2>
            <Button asChild variant="outline">
              <Link to="/superadmin">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Super Admin
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <TeamBreadcrumb team={team} />
        
        <TeamPageHeader 
          team={team}
          onEditClick={teamEditHook.openDialog}
          onDeleteClick={handleDelete}
        />

        <TeamInfoCard team={team} playerCount={playerCount} />

        {/* Payment Dashboard */}
        <div className="space-y-6 mb-8">
          <PaymentDashboard clubId={team?.clubs?.id} />
          <TransactionHistoryTable clubId={team?.clubs?.id} />
        </div>

        <TeamManagementTabs 
          team={team} 
          teamId={teamId!} 
          playerCount={playerCount} 
        />

        {/* Team Edit Dialog */}
        {team && (
          <TeamEditDialog
            isOpen={teamEditHook.isDialogOpen}
            onOpenChange={teamEditHook.setIsDialogOpen}
            team={team}
            initialData={{
              ...teamEditHook.initialData,
              name: teamEditHook.initialData.name || ''
            }}
            isSubmitting={teamEditHook.isSubmitting}
            onSubmit={teamEditHook.handleSubmit}
            onClose={teamEditHook.handleDialogClose}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <TeamDeleteDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          teamName={team.name}
          confirmText={deleteConfirmText}
          onConfirmTextChange={setDeleteConfirmText}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={deleteTeamMutation.isPending}
        />
      </div>
    </div>
  );
};

export default SuperAdminTeam;
